import React, { useEffect, useState } from 'react';
import { useParams, Navigate } from 'react-router-dom';
import type { Page, Settings } from '../../types';
import { useDocumentBySlug } from '../../hooks/useDocumentBySlug';
import { useSEO } from '../../hooks/useSEO';
import OptimizedImage from '../../components/OptimizedImage';

interface GenericPageProps {
    settings: Settings;
}

// Layout Templates
const StandardLayout: React.FC<{ page: Page }> = ({ page }) => (
    <div className="bg-brand-light text-brand-dark pt-32 pb-16">
        <div className="container mx-auto px-6 max-w-4xl">
            <h1 className="text-5xl md:text-6xl font-extrabold text-center mb-16 tracking-tight font-heading">{page.title}</h1>
            <div className="prose prose-lg max-w-none text-gray-800 leading-relaxed font-body" dangerouslySetInnerHTML={{ __html: page.content }}></div>
        </div>
    </div>
);

const TwoColumnLayout: React.FC<{ page: Page }> = ({ page }) => (
    <div className="bg-brand-light text-brand-dark pt-32 pb-16">
        <div className="container mx-auto px-6 max-w-6xl">
            <h1 className="text-5xl md:text-6xl font-extrabold text-center mb-16 tracking-tight font-heading">{page.title}</h1>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-12 items-start">
                <div className="md:col-span-1">
                    {page.featureImageUrl && (
                        <OptimizedImage
                            src={page.featureImageUrl}
                            size={800}
                            alt={page.title}
                            className="w-full h-auto object-cover grayscale"
                        />
                    )}
                </div>
                <div className="md:col-span-2 prose prose-lg max-w-none text-gray-800 leading-relaxed font-body" dangerouslySetInnerHTML={{ __html: page.content }}></div>
            </div>
        </div>
    </div>
);

const HeroLayout: React.FC<{ page: Page }> = ({ page }) => (
    <div>
        <div className="h-[60vh] w-full relative flex items-center justify-center text-center text-white pt-24 bg-brand-dark">
            {page.featureImageUrl && (
                <>
                    <OptimizedImage src={page.featureImageUrl} size={1920} alt={page.title} className="absolute inset-0 w-full h-full object-cover opacity-50" />
                    <div className="absolute inset-0 bg-black bg-opacity-40"></div>
                </>
            )}
            <div className="relative z-10 p-8">
                <h1 className="text-5xl md:text-7xl font-extrabold my-4 tracking-tight font-heading">{page.title}</h1>
            </div>
        </div>
        <div className="bg-brand-light text-brand-dark py-16">
            <div className="container mx-auto px-6 max-w-4xl">
                <div className="prose prose-lg max-w-none text-gray-800 leading-relaxed font-body" dangerouslySetInnerHTML={{ __html: page.content }}></div>
            </div>
        </div>
    </div>
);

const ContactSplitLayout: React.FC<{ page: Page, settings: Settings }> = ({ page, settings }) => {
    const [formData, setFormData] = useState({ name: '', email: '', message: '' });
    const [submitted, setSubmitted] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState('');

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        setSubmitError('');

        const useResend = settings.emailService === 'resend';

        if (!useResend && !settings.formspreeEndpoint) {
            console.error("Contact form endpoint is not configured in settings.");
            setSubmitError("The contact form is not configured. Please contact the site administrator.");
            return;
        }

        setIsSubmitting(true);
        try {
            let response;
            if (useResend) {
                response = await fetch('/api/send-email', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({
                        name: formData.name,
                        email: formData.email,
                        message: formData.message,
                        toEmail: settings.email,
                        senderEmail: settings.resendSenderEmail
                    })
                });
            } else {
                response = await fetch(settings.formspreeEndpoint!, {
                    method: 'POST',
                    body: new FormData(e.target as HTMLFormElement),
                    headers: { 'Accept': 'application/json' }
                });
            }

            if (response.ok) {
                setSubmitted(true);
                setFormData({ name: '', email: '', message: '' });
            } else {
                const data = await response.json();
                if (useResend) {
                    setSubmitError(data.error || "An unexpected error occurred. Please try again.");
                } else if (data.errors) {
                    setSubmitError(data.errors.map((error: any) => error.message).join(', '));
                } else {
                    setSubmitError("An unexpected error occurred. Please try again.");
                }
            }
        } catch (error) {
            setSubmitError("There was a problem sending your message. Please check your network connection.");
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="bg-brand-dark text-brand-light min-h-screen flex items-center pt-24 pb-16 font-body">
            <div className="container mx-auto px-6 grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
                {/* Left Side: Info & Form */}
                <div className="py-8">
                    <h1 className="text-5xl md:text-6xl font-extrabold tracking-tight font-heading">{page.title}</h1>
                    <div className="mt-4 text-lg text-gray-300 prose prose-invert" dangerouslySetInnerHTML={{ __html: page.content }}></div>
                    
                    <div className="mt-8 pt-8 border-t border-gray-700 space-y-4 text-gray-300">
                        <p>{settings.address}</p>
                        <p>{settings.email}</p>
                        <p>{settings.phone}</p>
                    </div>

                    <div className="mt-8">
                         {submitted ? (
                            <div className="bg-brand-light text-brand-dark p-8 text-center">
                                <h2 className="text-2xl font-bold mb-2 font-heading">Thank You!</h2>
                                <p>Your message has been sent. We will get back to you shortly.</p>
                            </div>
                        ) : (
                            <form onSubmit={handleSubmit} className="w-full space-y-6">
                                {/* Honeypot field for spam prevention with Formspree */}
                                <div style={{ position: 'absolute', left: '-5000px' }} aria-hidden="true">
                                    <input type="text" name="_gotcha" tabIndex={-1} autoComplete="off" />
                                </div>
                                <div>
                                    <label htmlFor="name" className="sr-only">Name</label>
                                    <input type="text" name="name" id="name" placeholder="Your Name" required value={formData.name} onChange={handleChange} className="w-full p-4 bg-gray-800 border border-gray-700 focus:ring-2 focus:ring-white focus:outline-none transition-all" />
                                </div>
                                <div>
                                    <label htmlFor="email" className="sr-only">Email</label>
                                    <input type="email" name="email" id="email" placeholder="Your Email" required value={formData.email} onChange={handleChange} className="w-full p-4 bg-gray-800 border border-gray-700 focus:ring-2 focus:ring-white focus:outline-none transition-all" />
                                </div>
                                <div>
                                    <label htmlFor="message" className="sr-only">Message</label>
                                    <textarea name="message" id="message" rows={5} placeholder="Your Message" required value={formData.message} onChange={handleChange} className="w-full p-4 bg-gray-800 border border-gray-700 focus:ring-2 focus:ring-white focus:outline-none transition-all"></textarea>
                                </div>
                                <button type="submit" disabled={isSubmitting} className="w-full py-4 text-center bg-white text-black font-bold uppercase tracking-widest hover:bg-gray-200 transition-colors font-header disabled:bg-gray-400">
                                    {isSubmitting ? 'Sending...' : 'Send Message'}
                                </button>
                                {submitError && <p className="text-red-500 text-sm text-center mt-4">{submitError}</p>}
                            </form>
                        )}
                    </div>
                </div>

                {/* Right Side: Map */}
                <div className="h-[70vh] w-full">
                    {settings.googleMapsUrl ? (
                        <iframe
                            src={settings.googleMapsUrl}
                            width="100%"
                            height="100%"
                            style={{ border: 0 }}
                            allowFullScreen={false}
                            loading="lazy"
                            referrerPolicy="no-referrer-when-downgrade"
                            className="grayscale"
                        ></iframe>
                    ) : (
                        <div className="w-full h-full bg-gray-700 flex items-center justify-center text-gray-400">
                            <p>Google Maps URL not configured.</p>
                        </div>
                    )}
                </div>
            </div>
            <style>{`.prose-invert p { color: #d1d5db; }`}</style>
        </div>
    );
}

const GenericPage: React.FC<GenericPageProps> = ({ settings }) => {
    const { slug } = useParams<{ slug: string }>();
    const { document: page, isLoading } = useDocumentBySlug<Page>('pages', slug);

    useSEO({
        title: page ? (page.seoTitle || page.title) : undefined,
        description: page?.seoDescription,
        ogImage: page?.featureImageUrl || settings.openGraph.imageUrl
    }, [page, settings]);

    useEffect(() => {
        window.scrollTo(0, 0);
    }, [slug]);

    if (isLoading) {
        return <div className="h-screen flex items-center justify-center"><p>Loading page...</p></div>;
    }

    if (!page || !page.published) {
        return <Navigate to="/" />;
    }

    const renderLayout = () => {
        switch (page.layoutTemplate) {
            case 'two-column': return <TwoColumnLayout page={page} />;
            case 'hero': return <HeroLayout page={page} />;
            case 'contact-split': return <ContactSplitLayout page={page} settings={settings} />;
            case 'standard':
            default:
                return <StandardLayout page={page} />;
        }
    };

    return (
        <div>
            {renderLayout()}
            <style>{`
                .prose p { margin-bottom: 1.25em; }
                .prose h1, .prose h2, .prose h3, .prose h4, .prose h5, .prose h6 { font-family: var(--font-family-heading); font-weight: 700; margin-top: 2em; margin-bottom: 1em; }
                .prose ul, .prose ol { margin-left: 1.5em; }
                .prose a { color: inherit; }
                .prose img { margin-top: 2em; margin-bottom: 2em; max-width: 100%; height: auto; border-radius: 0.25rem; }
            `}</style>
        </div>
    );
};

export default GenericPage;